import Decimal from 'decimal.js';

// Match customer-invoices/utils/invoiceCalculations.js on the server.
const Money = Decimal.clone({ precision: 20, rounding: Decimal.ROUND_HALF_UP });
const decimal = (value) => new Money(Number.isFinite(Number(value)) ? (value || 0) : 0);

/**
 * One place for the money arithmetic the invoice editor shows.
 *
 * The line table, the totals block and the sticky action bar all display
 * amounts derived from the same rows; before this they each did their own
 * float math and could disagree by an öre. Every line amount and every moms
 * amount is rounded to two decimals before it is summed, so what the user
 * reads in a row is exactly what was added into the total.
 */

export const round2 = (value) => decimal(value).toDecimalPlaces(2).toNumber();

/** '25%' | '25' | 25 | undefined → 25 | 0 */
export const parseVatRate = (rate) => parseFloat(String(rate ?? '').replace('%', '')) || 0;

export const lineAmount = (item) => {
    if (item?.type === 'Text') return 0;
    return decimal(item?.quantity).times(decimal(item?.unitprice)).toDecimalPlaces(2).toNumber();
};

/**
 * @param {Array} items invoice rows ({ quantity, unitprice, tax_rate })
 * @param {{ roundTotal?: boolean, isTaxable?: boolean }} options
 *   isTaxable=false (omvänd skattskyldighet, undantag, utanför tillämpningsområdet)
 *   means the seller posts no output VAT: rows keep their stored tax_rate but
 *   no moms is computed or shown.
 */
export const calculateInvoiceTotals = (items = [], { roundTotal = false, isTaxable = true } = {}) => {
    const taxGroups = {};
    let subtotal = 0;

    items.forEach((item) => {
        if (item.type === 'Text') return;
        const unroundedAmount = decimal(item.quantity).times(decimal(item.unitprice));
        const amount = lineAmount(item);
        subtotal = decimal(subtotal).plus(amount).toNumber();

        if (isTaxable) {
            const rate = parseVatRate(item.tax_rate);
            if (!taxGroups[rate]) taxGroups[rate] = { subtotal: 0, tax: 0 };
            taxGroups[rate].subtotal = decimal(taxGroups[rate].subtotal).plus(amount).toNumber();
            // VAT is rounded per line, from the unrounded net line amount.
            const lineTax = unroundedAmount.times(rate).dividedBy(100).toDecimalPlaces(2);
            taxGroups[rate].tax = decimal(taxGroups[rate].tax).plus(lineTax).toNumber();
        }
    });

    const totalTax = Object.values(taxGroups).reduce((sum, group) => sum.plus(group.tax), new Money(0)).toNumber();
    const grandTotal = decimal(subtotal).plus(totalTax).toNumber();

    let roundingAmount = 0;
    let finalTotal = grandTotal;
    if (roundTotal && grandTotal !== 0) {
        finalTotal = decimal(grandTotal).toDecimalPlaces(0).toNumber();
        roundingAmount = decimal(finalTotal).minus(grandTotal).toNumber();
    }

    return { taxGroups, subtotal, totalTax, grandTotal, roundingAmount, finalTotal };
};

export const formatMoney = (value, currency = 'SEK') => new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
}).format(Number(value) || 0);
